import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, MessageSquare, ArrowUpRight, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  under_review: 'bg-blue-100 text-blue-700 border-blue-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  needs_revision: 'bg-orange-100 text-orange-700 border-orange-200',
};

const actionLabels = {
  approved: { icon: CheckCircle, color: 'text-emerald-600' },
  rejected: { icon: XCircle, color: 'text-red-600' },
  commented: { icon: MessageSquare, color: 'text-blue-600' },
  escalated: { icon: ArrowUpRight, color: 'text-purple-600' },
  sent_back: { icon: RotateCcw, color: 'text-orange-600' },
};

export default function SubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const canReview = user?.role === 'admin' || user?.role === 'manager';
  const isTerminal = submission?.status === 'approved' || submission?.status === 'rejected';

  useEffect(() => {
    api.get(`/submissions/${id}`).then(r => setSubmission(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [id]);

  const handleReview = async (action) => {
    setSubmitting(true);
    try {
      await api.post(`/submissions/${id}/review`, { action, comment });
      toast.success(`Submission ${action}`);
      setComment('');
      const res = await api.get(`/submissions/${id}`);
      setSubmission(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="font-data text-sm text-muted-foreground">Loading...</p></div>;
  if (!submission) return <div className="text-center py-20 text-muted-foreground">Submission not found</div>;

  const mapped = submission.mapped_data || {};
  const raw = submission.raw_data || {};
  const unmapped = submission.unmapped_data || {};
  const reviews = submission.reviews || [];

  return (
    <div data-testid="submission-detail-page">
      <Button variant="ghost" className="mb-4 -ml-2 text-muted-foreground rounded-sm" onClick={() => navigate('/submissions')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Submissions
      </Button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold tracking-tight">{submission.form_name}</h1>
            <Badge className={`rounded-sm text-xs ${statusColors[submission.status] || ''}`}>{submission.status?.replace('_', ' ')}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Submitted by {submission.submitted_by_name} &middot; {format(new Date(submission.created_at), 'dd MMM yyyy, HH:mm')}
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] rounded-sm">{submission.form_type}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Data */}
        <div className="lg:col-span-2 space-y-4">
          {/* Mapped Data */}
          <Card className="border border-border rounded-sm shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base font-semibold">Mapped Data</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(mapped).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(mapped).map(([key, val]) => (
                    <div key={key} className="border-b border-border pb-3">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">{key.replace(/_/g, ' ')}</p>
                      <p className="font-data text-sm font-semibold">{val !== null && val !== undefined ? String(val) : '--'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No field mappings configured. Raw data is stored below.</p>
              )}
            </CardContent>
          </Card>

          {/* Raw Data (Collapsible) */}
          <Card className="border border-border rounded-sm shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base font-semibold">Raw Webhook Data</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowRaw(!showRaw)} data-testid="toggle-raw-data" className="text-xs rounded-sm">
                  {showRaw ? 'Collapse' : 'Expand'}
                </Button>
              </div>
            </CardHeader>
            {showRaw && (
              <CardContent>
                <pre className="bg-slate-50 border border-border rounded-sm p-4 text-xs font-data overflow-auto max-h-80">{JSON.stringify(raw, null, 2)}</pre>
                {Object.keys(unmapped).length > 0 && (
                  <>
                    <Separator className="my-4" />
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2">Unmapped Fields</p>
                    <pre className="bg-slate-50 border border-border rounded-sm p-4 text-xs font-data overflow-auto max-h-40">{JSON.stringify(unmapped, null, 2)}</pre>
                  </>
                )}
              </CardContent>
            )}
          </Card>
        </div>

        {/* Right: Review */}
        <div className="space-y-4">
          {/* Review Workflow */}
          <Card className="border border-border rounded-sm shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base font-semibold">Review Workflow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Priority</span>
                  <Badge variant="secondary" className={`text-[10px] rounded-sm ${submission.priority === 'high' ? 'bg-red-100 text-red-700' : submission.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{submission.priority}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Review Level</span>
                  <span className="font-data text-xs">{submission.current_review_level || 0} / {submission.max_review_level || 1}</span>
                </div>
              </div>
              <Separator className="my-3" />

              {/* Reviews timeline */}
              {reviews.length > 0 ? (
                <div className="space-y-3">
                  {reviews.map(r => {
                    const ActionIcon = actionLabels[r.action]?.icon || MessageSquare;
                    const iconColor = actionLabels[r.action]?.color || 'text-gray-500';
                    return (
                      <div key={r.id} className="flex items-start gap-3 p-3 bg-slate-50 border border-border rounded-sm">
                        <ActionIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{r.reviewer_name} <span className="font-normal text-muted-foreground">{r.action}</span></p>
                          {r.comment && <p className="text-sm mt-1 text-muted-foreground">{r.comment}</p>}
                          <p className="font-data text-[10px] text-muted-foreground mt-1">{format(new Date(r.created_at), 'dd MMM yyyy, HH:mm')}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No reviews yet</p>
              )}

              {/* Review Actions */}
              {canReview && !isTerminal && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <Textarea
                      data-testid="review-comment"
                      placeholder="Add a comment..."
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      className="rounded-sm text-sm resize-none"
                      rows={3}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Button data-testid="approve-btn" size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleReview('approved')} disabled={submitting}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button data-testid="reject-btn" size="sm" variant="destructive" className="rounded-sm" onClick={() => handleReview('rejected')} disabled={submitting}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      <Button data-testid="comment-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => handleReview('commented')} disabled={submitting || !comment}>
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Comment
                      </Button>
                      <Button data-testid="escalate-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => handleReview('escalated')} disabled={submitting}>
                        <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Escalate
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
